import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { WorkflowStep } from '../types';

const WorkflowDemo = () => {
  const [activeWorkflow, setActiveWorkflow] = useState<'whatsapp' | 'email'>('whatsapp');

  const whatsappWorkflow: WorkflowStep[] = [
    {
      id: "1",
      icon: "MessageSquare",
      title: "Customer Inquiry",
      description: "Customer sends message via WhatsApp Business",
      toolLogo: "https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
    },
    {
      id: "2",
      icon: "Brain",
      title: "AI Intent Analysis",
      description: "GPT-4 analyzes the message to understand if it's a sales lead or support query",
      toolLogo: "https://upload.wikimedia.org/wikipedia/commons/4/4d/OpenAI_Logo.svg"
    },
    {
      id: "3",
      icon: "Database",
      title: "CRM Lookup",
      description: "We check your HubSpot/Salesforce to see if they are an existing customer",
      toolLogo: "https://upload.wikimedia.org/wikipedia/commons/1/15/HubSpot_Logo.svg"
    },
    {
      id: "4",
      icon: "Send",
      title: "Personalized Reply",
      description: "AI drafts a human-like response using their name and order history",
      toolLogo: "https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
    }
  ];

  const emailWorkflow: WorkflowStep[] = [
    {
      id: "1",
      icon: "ShoppingCart",
      title: "Cart Abandoned",
      description: "Customer adds item to cart but leaves without paying",
      toolLogo: "https://upload.wikimedia.org/wikipedia/commons/0/0e/Shopify_logo_2018.svg"
    },
    {
      id: "2",
      icon: "Clock",
      title: "1-Hour Delay",
      description: "System waits 1 hour to see if they return naturally",
      toolLogo: "https://www.make.com/favicon.ico"
    },
    {
      id: "3",
      icon: "Mail",
      title: "Recovery Email",
      description: "Sends a personalized email with a photo of the item they left behind",
      toolLogo: "https://ssl.gstatic.com/ui/v1/icons/mail/rfr/gmail.ico"
    },
    {
      id: "4",
      icon: "Target",
      title: "Follow-up Offer",
      description: "If no purchase in 24h, sends a 10% discount code automatically",
      toolLogo: "https://www.make.com/favicon.ico"
    }
  ];

  const currentWorkflow = activeWorkflow === 'whatsapp' ? whatsappWorkflow : emailWorkflow;

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            See Automation in Action
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
            We don't just "send messages." We build intelligent logic flows.
          </p>

          <div className="inline-flex bg-muted p-1 rounded-xl">
            <button
              onClick={() => setActiveWorkflow('whatsapp')}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                activeWorkflow === 'whatsapp' 
                  ? 'bg-green-600 text-white shadow-sm' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon name="MessageSquare" size={16} />
                WhatsApp Flow
              </div>
            </button>
            <button
              onClick={() => setActiveWorkflow('email')}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                activeWorkflow === 'email' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon name="Mail" size={16} />
                Email Flow
              </div>
            </button>
          </div>
        </div>

        <div className="max-w-4xl mx-auto relative">
          {/* Vertical Line Background */}
          <div className="absolute left-8 top-8 bottom-8 w-0.5 bg-border -z-10 md:left-1/2 md:-ml-px" />

          {currentWorkflow.map((step, index) => (
            <div key={step.id} className={`relative flex items-center gap-8 mb-12 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
              
              {/* Icon Bubble (Center) */}
              <div className="flex-shrink-0 w-16 h-16 bg-background border-4 border-muted rounded-full flex items-center justify-center z-10 shadow-sm">
                <Icon name={step.icon} size={24} className={activeWorkflow === 'whatsapp' ? 'text-green-600' : 'text-blue-600'} />
              </div>

              {/* Content Card */}
              <div className="flex-1 bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow relative">
                {/* Arrow pointing to center */}
                <div className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-card border-border transform rotate-45 
                  ${index % 2 === 0 ? '-left-2 border-l border-b md:right-auto md:-left-2' : '-left-2 border-l border-b md:left-auto md:-right-2 md:border-l-0 md:border-b-0 md:border-r md:border-t'}
                `} />
                
                <div className="flex justify-between items-start mb-2">
                  <span className={`text-xs font-bold uppercase tracking-wide ${activeWorkflow === 'whatsapp' ? 'text-green-600' : 'text-blue-600'}`}>
                    Step 0{index + 1}
                  </span>
                  {step.toolLogo && (
                    <Image src={step.toolLogo} alt="Tool Logo" className="w-6 h-6 object-contain opacity-80" />
                  )}
                </div>
                
                <h3 className="text-lg font-bold text-foreground mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
              </div>

              {/* Empty space for opposite side on desktop */}
              <div className="hidden md:block flex-1" />
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default WorkflowDemo;